import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

/**
 * NavProgress
 * ───────────
 * Slim top-of-page progress bar (à la YouTube / NProgress).
 * - Shows while a route change is in flight.
 * - Also tracks axios request activity so slow API loads get a bar.
 * - Auto-hides ~250ms after last activity.
 */
export default function NavProgress() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const activeCount = useRef(0);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const location = useLocation();

  const start = () => {
    activeCount.current += 1;
    setVisible(true);
    // Ramp progress to ~85% while activity is in-flight
    let p = 8;
    setProgress(p);
    const tick = () => {
      p = Math.min(85, p + Math.max(0.5, (85 - p) * 0.04));
      setProgress(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  };

  const done = () => {
    activeCount.current = Math.max(0, activeCount.current - 1);
    if (activeCount.current > 0) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setProgress(100);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 280);
  };

  // Route-change progress
  useEffect(() => {
    start();
    const t = setTimeout(done, 380);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Axios request interceptors
  useEffect(() => {
    const reqId = axios.interceptors.request.use((c) => { start(); return c; });
    const resId = axios.interceptors.response.use(
      (r) => { done(); return r; },
      (e) => { done(); return Promise.reject(e); }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  if (!visible) return null;
  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-[999] pointer-events-none"
      style={{ height: "2.5px" }}
      data-testid="nav-progress"
    >
      <div
        className="h-full transition-[width] duration-200 ease-out"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg, #1B6FE0 0%, #7FB1FF 60%, #1B6FE0 100%)",
          boxShadow: "0 0 10px rgba(27,111,224,0.6)",
        }}
      />
    </div>
  );
}
